import { useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { MenuItem, ListItemIcon, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';

import { graphql, type DocumentType } from '@/gql';

import { nonFalsey } from '@/utils/nonFalsey';

import { SendArticleAsEmailDialog } from './send-article-menu-item/SendArticleAsEmailDialog';

const SEND_ARTICLE_AS_EMAIL_MENU_ITEM_QUERY = graphql(`
  query SendArticleAsEmailMenuItemQuery($serviceId: ID!) {
    service(id: $serviceId) {
      _id
      name
      editableByUser(feature: "news")
      reportQueriesForEmailing {
        ...sendArticleAsEmailReportQueryFields
      }
    }
  }
`);

const SEND_ARTICLE_AS_EMAIL_MENU_ITEM_ARTICLE_FRAGMENT = graphql(`
  fragment sendArticleAsEmailMenuItemArticleFields on Article {
    ...sendArticleAsEmailDialogArticleFields
  }
`);

interface Props {
  article: DocumentType<
    typeof SEND_ARTICLE_AS_EMAIL_MENU_ITEM_ARTICLE_FRAGMENT
  >;
  onClose: () => void;
}

export const SendArticleAsEmailMenuItem = ({ article, onClose }: Props) => {
  const serviceId = article?.feed?.owner?._id;

  const [dialogOpen, setDialogOpen] = useState(false);

  const { data } = useQuery(SEND_ARTICLE_AS_EMAIL_MENU_ITEM_QUERY, {
    variables: {
      serviceId: serviceId ?? '',
    },
    skip: !serviceId,
    fetchPolicy: 'cache-and-network',
  });

  const { service } = data || {};

  if (!data?.service) {
    return null;
  }

  const { editableByUser, reportQueriesForEmailing } = service || {};

  const reportQueries = reportQueriesForEmailing?.filter(nonFalsey) ?? [];

  if (!editableByUser) {
    return null;
  }

  return (
    <>
      <MenuItem onClick={() => setDialogOpen(true)}>
        <ListItemIcon>
          <EmailIcon />
        </ListItemIcon>
        <Typography variant="inherit">Send as an email</Typography>
      </MenuItem>
      <SendArticleAsEmailDialog
        article={article}
        reportQueriesForEmailing={reportQueries}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          onClose();
        }}
      />
    </>
  );
};
